import React, { useCallback } from 'react';
import classNames from 'classnames/bind';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

// Components
import { ArrowButton } from 'components';
import { useHistory } from 'react-router-dom';
import { Text } from '@beef/ui-kit';
import { CtnBlock } from './components/CtnBlock';
import { CancelInvite } from './components/CancelInvite';
import { AvailableSetupLimit } from './components/AvailableSetupLimit';
import { NoLimitsAvailable } from './components/NoLimitsAvailable';

// Constants
import { INVITATION_STATUS, PAGE_PATHS, POPUP_NAMES } from '../../constants';

// Utils
import { setActiveStep, setNewLimits, setPopupStatus } from '../../store/actions';
import { maskPhoneNumber } from '../../../../utils/validators';
import { getInstallationLimits } from '../../utils';

// Styles
import styles from './styles.pcss';
import { DiscountDescription } from './components/DiscountDescription';
import { Price } from '../../components/Price';

const cn = classNames.bind(styles);

const ManageInviteNumber = ({ id }) => {
  const history = useHistory();
  const { invites, content } = useSelector((state) => state.family);
  const dispatch = useDispatch();

  const chosenChild = invites.find((invite) => invite.ctn === id);
  const returnToFamilyList = () => history.push(PAGE_PATHS.information);

  const handleClick = (name) => {
    dispatch(setPopupStatus(true));
    dispatch(setActiveStep(name));
  };

  const handleDeleteBtnClick = useCallback(() => {
    handleClick(POPUP_NAMES.delete);
  }, []);

  const handleDetailsBtnClick = useCallback(() => {
    handleClick(POPUP_NAMES.terms);
  }, []);

  const handleCancelBtnClick = useCallback(() => {
    handleClick(POPUP_NAMES.cancelInvitation);
  }, []);

  const handleSaveLimitsBtnClick = useCallback((newLimits, ctn, isMax) => {
    handleClick(POPUP_NAMES.saveLimits);
    const installationLimits = getInstallationLimits(newLimits, chosenChild.accumulators);
    dispatch(setNewLimits({ ...installationLimits, ctn, isMax }));
  }, [chosenChild.accumulators]);

  const { discountDescription, price, unit } = chosenChild || {};

  const accessStatusText = chosenChild?.limitAccessStatusText;
  const hasShowLimitStatus = chosenChild?.status !== 'Sent' && accessStatusText;

  return (
    <section className={cn('control-panel')}>
      <div className={cn('control-panel__return-button')}>
        <ArrowButton onClick={returnToFamilyList} text="Все номера" />
      </div>
      <div className={cn('row')}>
        {chosenChild && (
          <div className={cn('control-panel__ctn')}>
            <CtnBlock
              ctn={maskPhoneNumber(chosenChild?.ctn)}
              status={chosenChild?.status}
              accessStatusText={chosenChild?.limitAccessStatusText}
              imageUrl={chosenChild?.image}
            />
          </div>
        )}
        <Price unit={unit}>{price}</Price>
      </div>

      {discountDescription && (
        <DiscountDescription className={cn('control-panel__discount')}>
          {discountDescription}
        </DiscountDescription>
      )}

      {!!chosenChild?.accumulators?.length && (chosenChild.status !== INVITATION_STATUS.sent) && (
        <>
          <AvailableSetupLimit
            accumulators={chosenChild.accumulators}
            isProcessing={chosenChild.isProcessing}
            handleDeleteBtnClick={handleDeleteBtnClick}
            handleDetailsBtnClick={handleDetailsBtnClick}
            handleSaveLimitsBtnClick={handleSaveLimitsBtnClick}
            ctn={chosenChild?.ctn}
            accessStatusText={accessStatusText}
            isShowLimitText={hasShowLimitStatus}
          />
        </>
      )}
      {!chosenChild?.accumulators?.length && (chosenChild.status !== INVITATION_STATUS.sent) && (
        <NoLimitsAvailable
          noLimitsAvailableText={content?.noLimitsAvailableText}
          handleDeleteBtnClick={handleDeleteBtnClick}
          showMoreTariffsUrl={content?.showMoreTariffsUrl}
        />
      )}
      {(chosenChild.status === INVITATION_STATUS.sent) && (
        <CancelInvite
          onClick={handleCancelBtnClick}
          invitationSentText={content?.invitationSentText}
        />
      )}
    </section>
  );
};

ManageInviteNumber.propTypes = {
  id: PropTypes.string,
};

export { ManageInviteNumber };
